export const OUTBOX_BATCH_SIZE = 50
export const PULL_PAGE_SIZE = 200
export const MAX_BACKOFF_MS = 15 * 60 * 1_000
export const PENDING_ALERT_AGE_MS = 15 * 60 * 1_000

export type EntityType =
  | 'event'
  | 'pin'
  | 'poll'
  | 'vote_intent'
  | 'photo'
  | 'album_video'
  | 'comment'
  | 'like'
  | 'challenge_completion'
  | 'conversion_preference'
  | 'anecdote'
  | 'notification_preference'
  | 'timeline_entry'
  | 'position_share'
  | 'position_sample'
  | 'challenge'
  | 'challenge_assignment'
  | 'reveal'
  | 'trip_archive'
  | 'notification'

export type MutationOperation = 'create' | 'update' | 'delete' | 'upsert'
export type SyncState = 'pending' | 'syncing' | 'synced' | 'failed' | 'conflict' | 'recovery'
export type ReceiptOutcome = 'applied' | 'duplicate' | 'rejected' | 'conflict'

export type LocalEntity = {
  key: string
  id: string
  tripId: string
  entityType: EntityType
  payload: Record<string, unknown>
  createdAtDevice: string
  updatedAtDevice: string
  updatedAtServer: string | null
  version: number
  deletedAt: string | null
  lastMutationId: string
  syncState: SyncState
  lastError: string | null
}

export type OutboxMutation = {
  mutationId: string
  entityType: EntityType
  entityId: string
  tripId: string
  operation: MutationOperation
  baseVersion: number
  payload: Record<string, unknown>
  createdAtDevice: string
  state: SyncState
  attemptCount: number
  nextAttemptAt: number
  lastErrorCode: string | null
  lastErrorMessage: string | null
}

export type ServerEntity = {
  entityType: EntityType
  entityId: string
  tripId: string
  payload: Record<string, unknown>
  version: number
  deletedAt: string | null
  updatedAtServer?: string
}

export type MutationReceipt = {
  mutationId: string
  outcome: ReceiptOutcome
  resultingVersion: number | null
  receivedAtServer: string
  errorCode: string | null
  errorMessage: string | null
  serverEntity?: ServerEntity
}

export type PullChange = ServerEntity & {
  cursor: number
  operation: MutationOperation
}

export type PullPage = {
  changes: PullChange[]
  nextCursor: number
  hasMore: boolean
}

export type ConflictHistory = {
  id: string
  entityType: EntityType
  entityId: string
  localPayload: Record<string, unknown>
  serverPayload: Record<string, unknown>
  localVersion: number
  serverVersion: number
  recordedAt: string
  replacingMutationId: string
}

export type SyncDiagnostic = {
  id: string
  createdAt: string
  phase: 'push' | 'pull' | 'recovery'
  entityType: EntityType | null
  outcome: string
  correlationId: string
  attemptCount: number
  durationMs: number
  message: string | null
}

export class TransientSyncError extends Error {
  readonly retryAfterMs: number | null

  constructor(message = 'Réseau momentanément indisponible.', retryAfterMs: number | null = null) {
    super(message)
    this.name = 'TransientSyncError'
    this.retryAfterMs = retryAfterMs
  }
}

export class AuthorizationSyncError extends Error {
  constructor(message = 'Session révoquée.') {
    super(message)
    this.name = 'AuthorizationSyncError'
  }
}

export function entityKey(entityType: EntityType, entityId: string): string {
  return `${entityType}:${entityId}`
}

export function computeBackoffMs(attemptCount: number, random = Math.random): number {
  const exponential = Math.min(MAX_BACKOFF_MS, 1_000 * 2 ** Math.max(0, attemptCount - 1))
  const jitter = 0.8 + random() * 0.4
  return Math.min(MAX_BACKOFF_MS, Math.round(exponential * jitter))
}

export function sanitizedSyncMessage(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const withoutSecrets = value
    .replace(/\b\d{4}\b/gu, '[redacted]')
    .replace(/(pin|trip.?code|activation.?secret)\s*[:=]\s*\S+/giu, '$1=[redacted]')
    .replace(/-?\d{1,3}\.\d{4,}\s*,\s*-?\d{1,3}\.\d{4,}/gu, '[coordinates redacted]')
  return withoutSecrets.slice(0, 240)
}
